import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight } from 'lucide-react'

import type { ConceptSummary } from '#/api/concepts'
import type { CorpusItem } from '#/api/corpus'
import type { PlatformQuestionnaire } from '#/api/questionnaire-guidance'
import { useCorpusLabels } from '#/components/corpus/labels'
import { SECTION_LINK_CLASS } from '#/components/page-header'
import { Badge } from '#/components/ui/badge'
import { TableFrame, tableRowLinkClass } from '#/components/ui/table'
import { formatCode } from '#/lib/format'
import { cn } from '#/lib/utils'

// The Corpus page's tabs, in order.
export const CORPUS_TABS = ['concepts', 'dataset', 'guidance'] as const
export type CorpusTab = (typeof CORPUS_TABS)[number]

// The corpus as an editorial list: paper on the canvas, linen hairlines
// between rows, each row one thing that opens.
export function CorpusList({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <TableFrame>
      <ul aria-label={label}>{children}</ul>
    </TableFrame>
  )
}

// Back to the Corpus, to the section a page was opened from.
export function CorpusBackLink({
  accountId,
  tab,
  children,
}: {
  accountId: string
  tab?: CorpusTab
  children: ReactNode
}) {
  return (
    <Link
      to="/accounts/$accountId/corpus"
      params={{ accountId }}
      search={tab ? { tab } : {}}
      className={cn(
        SECTION_LINK_CLASS,
        'w-fit gap-1.5 hover:[&_svg]:-translate-x-0.5',
      )}
    >
      <ArrowLeft aria-hidden="true" />
      {children}
    </Link>
  )
}

// The name and summary on the left; on the right, a quiet column of a word
// or two, never a pill. Every row opens something, and tints on hover.
// `CORPUS_ROW_CLASS` is the row's frame alone, for a row of another shape.
export const CORPUS_ROW_CLASS =
  'group relative flex flex-col gap-3 border-b border-border px-5 py-5 transition-colors last:border-b-0 hover:bg-muted/40 sm:px-6'
const ROW_CLASS = cn(
  CORPUS_ROW_CLASS,
  'md:grid md:grid-cols-[minmax(0,1fr)_12rem] md:items-start md:gap-8',
)

// The row's name, its arrow leaning in on hover.
function RowTitle({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 font-serif text-display-sm font-normal text-title">
      {children}
      <ArrowRight
        aria-hidden="true"
        className="size-4 text-muted-foreground opacity-0 motion-safe:transition-[opacity,translate] group-hover:translate-x-0.5 group-hover:opacity-100"
      />
    </span>
  )
}

function RowSummary({ children }: { children: ReactNode }) {
  return children ? (
    <p className="line-clamp-2 max-w-2xl text-body-sm text-muted-foreground">
      {children}
    </p>
  ) : null
}

function RowAside({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-caption-md text-muted-foreground md:flex-col md:items-end md:pt-1.5 md:text-right">
      {children}
    </div>
  )
}

// A concept opens its own page: its name, what it is, and where its document
// stands.
export function ConceptRow({
  accountId,
  concept,
}: {
  accountId: string
  concept: ConceptSummary
}) {
  const { t } = useTranslation()
  const { codeName, conceptName } = useCorpusLabels()
  return (
    <li className={ROW_CLASS}>
      <div className="flex min-w-0 flex-col gap-1.5">
        <Link
          to="/accounts/$accountId/corpus/$conceptId"
          params={{ accountId, conceptId: concept.id }}
          className={tableRowLinkClass}
        >
          <RowTitle>{conceptName(concept.id)}</RowTitle>
        </Link>
        <RowSummary>{concept.summary}</RowSummary>
      </div>
      <RowAside>
        <span>{codeName('status', concept.meta.status)}</span>
        <span className="tabular-nums">
          {t('corpus.version', { version: concept.version })}
        </span>
      </RowAside>
    </li>
  )
}

// A catalogue holding — a dataset, say — opens its detail. Beside it, whether
// Annwn stores a copy and under what licence, and whether it is a sample.
export function HoldingRow({
  item,
  onOpen,
}: {
  item: CorpusItem
  onOpen: () => void
}) {
  const { codeName } = useCorpusLabels()
  return (
    <li className={ROW_CLASS}>
      <div className="flex min-w-0 flex-col gap-1.5">
        <button
          type="button"
          onClick={onOpen}
          className={cn(tableRowLinkClass, 'text-left')}
        >
          <RowTitle>{item.name}</RowTitle>
        </button>
        <RowSummary>{item.summary}</RowSummary>
        <span className="text-caption-md text-muted-foreground">
          {item.publisher}
        </span>
      </div>
      <RowAside>
        {item.sample ? <SampleBadge /> : null}
        <span className="text-foreground">
          {codeName('storage', item.holding)}
        </span>
        <span>{codeName('licences', item.licence)}</span>
      </RowAside>
    </li>
  )
}

// Marks a demonstration entry, wherever it shows.
export function SampleBadge() {
  const { t } = useTranslation()
  return <Badge variant="neutral">{t('corpus.sample')}</Badge>
}

// A questionnaire's guidance opens its reader.
export function GuidanceRow({
  accountId,
  questionnaire,
}: {
  accountId: string
  questionnaire: PlatformQuestionnaire
}) {
  const { t } = useTranslation()
  return (
    <li className={ROW_CLASS}>
      <div className="flex min-w-0 flex-col gap-1.5">
        <Link
          to="/accounts/$accountId/corpus/guidance/$code"
          params={{ accountId, code: questionnaire.code }}
          className={tableRowLinkClass}
        >
          <RowTitle>
            {t('corpus.guidance.name', { questionnaire: questionnaire.name })}
          </RowTitle>
        </Link>
        <span className="text-body-sm text-muted-foreground">
          {formatCode(questionnaire.code)}
        </span>
      </div>
    </li>
  )
}
